import { createContext, useContext, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useOfferBuilderContext } from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import usePricingSuggestion from '@/enterprise/data/usePricingSuggestion';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';

// ---------------------------------------------------------------------------
// Context — lets AcquisitionOfferSession provide the onHydrated callback to
// HydrateAcquisitionRates without prop-drilling through the shared
// OfferReviewLayoutComponent.
// ---------------------------------------------------------------------------

interface AcquisitionHydrationContextValue {
  onHydrated: (hasRates: boolean) => void;
}

const AcquisitionHydrationContext = createContext<AcquisitionHydrationContextValue | undefined>(undefined);

/**
 * Provides a callback that is triggered once acquisition pricing hydration completes
 * allows HydrateAcquisitionRates to signal whether any DISC rates were returned
 * uses context to avoid passing the callback through the shared OfferReviewLayoutComponent
 */
export const AcquisitionHydrationProvider = ({
  children,
  onHydrated,
}: {
  children: React.ReactNode;
  onHydrated: (hasRates: boolean) => void;
}) => <AcquisitionHydrationContext.Provider value={{ onHydrated }}>{children}</AcquisitionHydrationContext.Provider>;

/** Read the onHydrated callback provided by AcquisitionHydrationProvider. */
const useAcquisitionHydrationCallback = () => {
  const context = useContext(AcquisitionHydrationContext);

  if (!context) {
    throw new Error('useAcquisitionHydrationCallback must be used within AcquisitionHydrationProvider');
  }

  return context.onHydrated;
};

/**
 * Inner component that uses useSuspenseQuery (via usePricingSuggestion). The suspension
 * is caught by OfferReviewLayout's QueryBoundaries, showing the review skeleton until
 * pricing is ready.
 *
 * Once pricing resolves, dispatches UPDATE_WORKING_TAKER_MARKETS_RATE for any markets with
 * a DISC trending rate, then calls onHydrated(hasRates) so the parent can:
 *   - unmount this component (dropping the pricing cache subscription before post-submit
 *     cache eviction fires)
 *   - redirect to build-offer if hasRates is false
 */
const AcquisitionRatesFetcher = ({
  selected,
  onHydrated,
}: {
  selected: TakerMarket[];
  onHydrated: (hasRates: boolean) => void;
}) => {
  const { dispatch } = useOfferBuilderContext();

  const { data: pricingData } = usePricingSuggestion({
    takerMarkets: selected,
    alwaysFetch: true,
  });

  useEffectOnce(() => {
    if (!pricingData) {
      onHydrated(false);
      return;
    }

    // Acquisition always uses one-time DISC pricing — build the rate update payload.
    const rateUpdates = selected.flatMap((takerMarket) => {
      const pricingForMarket = pricingData.supplierMarkets.find(
        (sm) => sm.marketUuid === takerMarket.marketUuid && sm.supplierDivisionUuid === takerMarket.takerDivisionUuid
      );
      const trendingRate = pricingForMarket?.discount?.oneTime?.trendingRate;

      if (trendingRate === undefined) {
        return [];
      }

      return [
        {
          takerMarketKey: `${takerMarket.marketUuid}-${takerMarket.takerDivisionUuid}`,
          rateInfo: {
            rate: trendingRate,
            rateType: 'DISC' as const,
            pricingRateUuid: pricingForMarket?.discount?.oneTime?.pricingRateUuid,
            pricingStrategy: 'trendingRate' as const,
            suggestedRate: trendingRate,
            suggestRateType: 'DISC' as const,
            projectedClearingPercentage: pricingForMarket?.discount?.oneTime?.projectedClearingPercentage,
          },
        },
      ];
    });

    if (rateUpdates.length > 0) {
      dispatch({ type: 'UPDATE_WORKING_TAKER_MARKETS_RATE', payload: rateUpdates });
    }

    onHydrated(rateUpdates.length > 0);
  });

  return null;
};

/**
 * Hydrates the acquisition offer builder with DISC trending rates from the pricing API.
 *
 * Reads onHydrated from AcquisitionHydrationContext (provided by AcquisitionOfferSession)
 * rather than accepting it as a prop, keeping the shared OfferReviewLayout free of
 * acquisition-specific dependencies.
 *
 * Renders AcquisitionRatesFetcher (which suspends via useSuspenseQuery while pricing loads)
 * until rates are dispatched, then returns null. Returning null unmounts AcquisitionRatesFetcher
 * and drops its pricing cache subscription. This means when removePricingSuggestionQueries fires
 * synchronously during post-submit refetch, there are no active subscribers to trigger a
 * suspension or skeleton flash before navigation to the confirmation page.
 *
 * Calls onHydrated(hasRates) when done. hasRates is true if at least one market returned a
 * DISC trending rate, false otherwise (caller should redirect to build-offer when false).
 */
const HydrateAcquisitionRates = () => {
  const {
    state: { selected },
  } = useOfferBuilderContext();
  const onHydrated = useAcquisitionHydrationCallback();
  const [isHydrated, setIsHydrated] = useState(false);

  if (isHydrated) {
    return null;
  }

  return (
    <AcquisitionRatesFetcher
      selected={selected}
      onHydrated={(hasRates) => {
        setIsHydrated(true);
        onHydrated(hasRates);
      }}
    />
  );
};

export default HydrateAcquisitionRates;
